import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAppSettings, APP_SETTING_KEYS } from '../../contexts/AppSettingsContext';

interface UserMenuProps {
  user: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
    personId?: number;
    hasDirectoryRecord?: boolean;
  };
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { getBool } = useAppSettings();
  const calendarPageEnabled = getBool(APP_SETTING_KEYS.calendarPageEnabled, false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = user.name
    .split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const getRoleDisplay = (role: string): string => {
    const roleMap: { [key: string]: string } = {
      superAdmin: 'Super Administrator',
      organizationAdmin: 'Organization Admin',
      viewer: 'Viewer'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role: string): string => {
    const colorMap: { [key: string]: string } = {
      superAdmin: 'text-aiu-red',
      organizationAdmin: 'text-aiu-blue dark:text-blue-400',
      viewer: 'text-gray-600 dark:text-gray-400'
    };
    return colorMap[role] || 'text-gray-600 dark:text-gray-400';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* A plain circle: Flowbite's Avatar puts className on a rounded-square
          wrapper, so its ring drew a permanent square outline */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center space-x-3 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-aiu-blue focus-visible:ring-offset-2 dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-gray-800"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gray-100 ring-2 ring-gray-200 transition-all group-hover:ring-aiu-blue dark:bg-gray-600 dark:ring-gray-600 dark:group-hover:ring-blue-400">
          {user.avatar ? (
            <img src={user.avatar} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{initials}</span>
          )}
        </span>
        <span className="hidden md:block text-sm text-gray-700 dark:text-gray-300 font-medium">
          {user.name.split(' ')[0]}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{user.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
              <p className={`text-xs font-medium ${getRoleColor(user.role)}`}>
                {getRoleDisplay(user.role)}
              </p>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {user.hasDirectoryRecord && user.personId && (
              <button
                onClick={() => {
                  navigate(`/persons/${user.personId}`);
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                My Profile
              </button>
            )}

            {calendarPageEnabled && (
              <button
                onClick={() => {
                  navigate('/me/calendar-feeds');
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                My Calendar Feeds
              </button>
            )}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                onLogout();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-aiu-red dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
